import { useMemo, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { AlarmClock, BellRing, CheckCheck, CheckCircle2, ChevronDown, ClipboardCheck, Clock, Info, Mail, Settings2 } from 'lucide-react';
import type { AttentionItem } from '@mailpilot/shared';
import { fullDate, relativeTime } from '../lib/format';
import { useRing } from '../lib/ring';
import { useAcknowledge, useAcknowledgeAll, useAttentionItems, useSnooze } from '../hooks/useAttention';
import { useSettings } from '../hooks/useMe';
import { Badge, Button, Card, EmptyState, ErrorState, Menu, Notice, Page, PageHeader, Skeleton, SkeletonLines, spring, useConfirm, type Tone } from '../components/ui';
import { RingStatusPanel } from '../components/reminders/RingStatusPanel';

const sourceLabel: Record<AttentionItem['sourceType'], { label: string; tone: Tone }> = {
  auto_send: { label: 'Automatic send', tone: 'primary' },
  attachment_request: { label: 'Attachment request', tone: 'warn' },
  send_failure: { label: 'Send failure', tone: 'danger' },
  reauth: { label: 'Account needs reconnecting', tone: 'danger' },
};

const SNOOZE_CHOICES = [15, 30, 60, 120];
const RECENT_ACK_LIMIT = 10;

const linkBtn =
  'inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-line bg-surface px-4 text-ui font-medium text-ink shadow-sm transition-colors hover:bg-raised';

function fmtMinutes(m: number): string {
  if (m < 60) return `${m} minutes`;
  const h = m / 60;
  return Number.isInteger(h) ? `${h} hour${h === 1 ? '' : 's'}` : `${m} minutes`;
}

export function RemindersPage() {
  const q = useAttentionItems('all');
  const ackAll = useAcknowledgeAll();
  const confirm = useConfirm();

  const groups = useMemo(() => {
    const items = q.data?.items ?? [];
    const byCreated = (a: AttentionItem, b: AttentionItem) => b.createdAt.localeCompare(a.createdAt);
    return {
      pending: items.filter((i) => i.status === 'pending').sort(byCreated),
      snoozed: items.filter((i) => i.status === 'snoozed').sort((a, b) => (a.snoozedUntil ?? '').localeCompare(b.snoozedUntil ?? '')),
      acknowledged: items
        .filter((i) => i.status === 'acknowledged')
        .sort((a, b) => (b.acknowledgedAt ?? '').localeCompare(a.acknowledgedAt ?? ''))
        .slice(0, RECENT_ACK_LIMIT),
    };
  }, [q.data]);

  const openCount = groups.pending.length + groups.snoozed.length;

  const onAckAll = async () => {
    const ok = await confirm({
      title: `Acknowledge all ${openCount} reminder${openCount === 1 ? '' : 's'}?`,
      message: 'This stops future rings for every pending and snoozed reminder on all your devices. It does not mark any email as replied or resolved.',
      confirmLabel: 'Acknowledge all',
    });
    if (ok) ackAll.mutate();
  };

  return (
    <Page>
      <PageHeader
        tone="salmon"
        icon={<BellRing className="h-5 w-5" />}
        title="Reminders"
        description="Automatic sends and items that need you keep ringing until you explicitly acknowledge them."
        actions={
          <>
            <Link to="/review" className={linkBtn}>
              <ClipboardCheck className="h-4 w-4" aria-hidden />
              AI Review
            </Link>
            {openCount > 1 && (
              <Button variant="primary" icon={<CheckCheck className="h-4 w-4" />} loading={ackAll.isPending} onClick={() => void onAckAll()}>
                Acknowledge all
              </Button>
            )}
          </>
        }
      />

      <div className="space-y-6">
        <Notice tone="info" icon={<Info className="h-4 w-4" aria-hidden />}>
          Opening an email, dismissing a notification, or marking mail as read does <strong>not</strong> acknowledge a reminder. Only <strong>“Seen / Acknowledged”</strong> stops future rings — on
          every device. Acknowledged is separate from replied or resolved.
        </Notice>

        {q.isPending ? (
          <Card className="space-y-4 p-6" aria-busy="true" aria-label="Loading reminders">
            <Skeleton className="h-5 w-40" />
            <SkeletonLines lines={3} />
            <Skeleton className="h-10 w-56 rounded-xl" />
          </Card>
        ) : q.isError ? (
          <Card>
            <ErrorState error={q.error} onRetry={() => void q.refetch()} title="Couldn't load reminders" />
          </Card>
        ) : (
          <>
            <Section title="Pending" icon={<BellRing className="h-3.5 w-3.5" aria-hidden />} count={groups.pending.length} tone="danger">
              {groups.pending.length === 0 ? (
                <Card>
                  <EmptyState
                    className="py-12"
                    icon={<CheckCircle2 className="h-6 w-6" />}
                    title="Nothing is ringing"
                    description="When MailPilot sends something automatically, or needs you to act, a reminder appears here and rings until you acknowledge it."
                    actions={
                      <Link to="/settings/notifications" className={linkBtn}>
                        <Settings2 className="h-4 w-4" aria-hidden />
                        Notification settings
                      </Link>
                    }
                  />
                </Card>
              ) : (
                <ItemList items={groups.pending} />
              )}
            </Section>

            {groups.snoozed.length > 0 && (
              <Section title="Snoozed" icon={<AlarmClock className="h-3.5 w-3.5" aria-hidden />} count={groups.snoozed.length} tone="warn">
                <ItemList items={groups.snoozed} />
              </Section>
            )}

            {groups.acknowledged.length > 0 && (
              <Section title="Recently acknowledged" icon={<CheckCircle2 className="h-3.5 w-3.5" aria-hidden />} count={groups.acknowledged.length} tone="success">
                <Card>
                  <ul className="p-2">
                    {groups.acknowledged.map((i) => (
                      <AcknowledgedRow key={i.id} item={i} />
                    ))}
                  </ul>
                </Card>
              </Section>
            )}
          </>
        )}

        <RingStatusPanel />

        <p className="text-center text-[0.8125rem] text-muted">
          Change the ringtone, volume, repeat interval and quiet hours in{' '}
          <Link to="/settings/notifications" className="inline-flex items-center gap-1 font-semibold text-primary-ink underline-offset-2 hover:underline">
            <Settings2 className="h-3.5 w-3.5" aria-hidden />
            Notification settings
          </Link>
          .
        </p>
      </div>
    </Page>
  );
}

function Section({ title, icon, count, tone, children }: { title: string; icon: ReactNode; count: number; tone: Tone; children: ReactNode }) {
  const id = `reminders-${title.split(' ')[0].toLowerCase()}`;
  return (
    <section aria-labelledby={id} className="space-y-3">
      <h2 id={id} className="flex items-center gap-2 px-1 text-[0.9375rem] font-semibold text-ink">
        <span className="text-muted">{icon}</span>
        {title}
        <Badge tone={count ? tone : 'neutral'}>{count}</Badge>
      </h2>
      {children}
    </section>
  );
}

function ItemList({ items }: { items: AttentionItem[] }) {
  return (
    <ul className="space-y-3">
      <AnimatePresence initial={false}>
        {items.map((item) => (
          <motion.li key={item.id} layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.98, transition: { duration: 0.18 } }} transition={spring}>
            <ReminderCard item={item} />
          </motion.li>
        ))}
      </AnimatePresence>
    </ul>
  );
}

function Meta({ label, children, title }: { label: string; children: ReactNode; title?: string }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <dt className="text-subtle">{label}</dt>
      <dd className="font-medium text-ink" title={title}>
        {children}
      </dd>
    </div>
  );
}

function ReminderCard({ item }: { item: AttentionItem }) {
  const navigate = useNavigate();
  const ring = useRing();
  const settings = useSettings();
  const ack = useAcknowledge();
  const snooze = useSnooze();
  const src = sourceLabel[item.sourceType];
  const pending = item.status === 'pending';
  const defaultSnooze = settings?.ring.snoozeMinutes;
  const titleId = `rem-${item.id}`;

  const snoozeItems = [
    ...(defaultSnooze && !SNOOZE_CHOICES.includes(defaultSnooze)
      ? [{ key: 'default', label: `${fmtMinutes(defaultSnooze)} (default)`, onSelect: () => snooze.mutate({ id: item.id, minutes: defaultSnooze }) }]
      : []),
    ...SNOOZE_CHOICES.map((m) => ({
      key: String(m),
      label: m === defaultSnooze ? `${fmtMinutes(m)} (default)` : fmtMinutes(m),
      active: m === defaultSnooze,
      onSelect: () => snooze.mutate({ id: item.id, minutes: m }),
    })),
  ];

  return (
    <Card aria-labelledby={titleId} role="article" className={clsx('hover-lift', pending && 'border-danger/25')}>
      <div className="flex items-start gap-4 p-6 pb-5">
        <span
          className={clsx(
            'mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px]',
            pending ? 'bg-danger-soft text-danger-ink' : 'bg-warn-soft text-warn-ink',
            pending && ring.ringingNow && 'ring-pulse',
          )}
          aria-hidden
        >
          {pending ? <BellRing className="h-5 w-5" /> : <AlarmClock className="h-5 w-5" />}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-col gap-1.5 sm:flex-row sm:items-start sm:gap-3">
            <h3 id={titleId} className="min-w-0 flex-1 text-[0.9375rem] font-semibold leading-snug text-ink">
              {item.title}
            </h3>
            <div className="flex shrink-0 flex-wrap items-center gap-1.5 sm:pt-0.5">
              <Badge tone={src.tone}>{src.label}</Badge>
              {pending && ring.ringingNow && <Badge tone="danger">Ringing now</Badge>}
            </div>
          </div>
          {item.body && <p className="mt-1.5 max-w-prose whitespace-pre-wrap text-[0.8125rem] leading-relaxed text-muted">{item.body}</p>}
          <dl className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs">
            <Meta label="Created" title={fullDate(item.createdAt)}>
              {relativeTime(item.createdAt)}
            </Meta>
            <Meta label="Rang">
              {item.ringCount} time{item.ringCount === 1 ? '' : 's'}
            </Meta>
            <Meta label="Last rang" title={item.lastRangAt ? fullDate(item.lastRangAt) : undefined}>
              {item.lastRangAt ? relativeTime(item.lastRangAt) : 'not yet'}
            </Meta>
            {pending ? (
              <Meta label="Next ring" title={item.nextRingAt ? fullDate(item.nextRingAt) : undefined}>
                {item.nextRingAt ? relativeTime(item.nextRingAt) : '—'}
              </Meta>
            ) : (
              <Meta label="Snoozed until" title={item.snoozedUntil ? fullDate(item.snoozedUntil) : undefined}>
                {item.snoozedUntil ? fullDate(item.snoozedUntil) : '—'}
              </Meta>
            )}
          </dl>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 border-t border-line/60 bg-raised/40 px-6 py-3.5">
        <Button variant="primary" icon={<CheckCircle2 className="h-4 w-4" />} loading={ack.isPending} onClick={() => ack.mutate(item.id)} aria-describedby={titleId}>
          Seen / Acknowledged
        </Button>
        <Menu
          label="Snooze reminder"
          align="left"
          items={snoozeItems}
          trigger={(p) => (
            <Button {...p} icon={<Clock className="h-4 w-4" />} iconRight={<ChevronDown className="h-4 w-4 text-muted" />} loading={snooze.isPending}>
              Snooze
            </Button>
          )}
        />
        {item.threadId && (
          <Button variant="ghost" icon={<Mail className="h-4 w-4" />} onClick={() => navigate(`/inbox/${item.threadId}`)} className="ml-auto">
            Open email
          </Button>
        )}
      </div>
    </Card>
  );
}

function AcknowledgedRow({ item }: { item: AttentionItem }) {
  const src = sourceLabel[item.sourceType];
  return (
    <li className="group flex flex-wrap items-center gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-raised">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-success-soft text-success-ink" aria-hidden>
        <CheckCircle2 className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[0.8125rem] font-medium text-ink">{item.title}</p>
        <p className="text-xs text-muted">
          {src.label} · Acknowledged{' '}
          <span title={item.acknowledgedAt ? fullDate(item.acknowledgedAt) : undefined}>{relativeTime(item.acknowledgedAt)}</span>
          {item.acknowledgedByDevice ? ` on ${item.acknowledgedByDevice}` : ''} · rang {item.ringCount}×
        </p>
      </div>
      {item.threadId && (
        <Link
          to={`/inbox/${item.threadId}`}
          className="inline-flex h-9 items-center gap-1.5 rounded-xl px-3 text-[0.8125rem] font-medium text-primary-ink opacity-0 transition-opacity hover:bg-primary-soft focus:opacity-100 group-hover:opacity-100"
        >
          <Mail className="h-4 w-4" aria-hidden />
          Open email
        </Link>
      )}
    </li>
  );
}
